import { queryOptions, useMutation } from '@tanstack/react-query'
import type { DerekExperimentRequest, DerekExperimentResult } from '@headline/schemas'
import { api } from './api'
import { fail } from './query-fail'

/**
 * The Derek tab's agreement for one issue: the judge's verdicts next to what the person did
 * (`GET /api/report-issues/:id/derek`). Cheap to recompute, so no long cache: a person removing
 * an article in the editor should see the buckets move on the next look.
 */
function derekAgreementQuery(issueId: string) {
  return queryOptions({
    queryKey: ['issue', issueId, 'derek'],
    queryFn: async () => {
      const res = await api.api['report-issues'][':id'].derek.$get({ param: { id: issueId } })
      if (!res.ok) return fail(res, 'Failed to load the Derek agreement')
      return res.json()
    },
    staleTime: 30 * 1000,
  })
}

/**
 * What the Derek tab compares the judge against: one of the report's own issues, or — for a report
 * the legacy portal still delivers — one of the portal's sends (`GET /api/reports/:id/derek/legacy`).
 */
export type DerekBase = { kind: 'issue'; id: string } | { kind: 'legacy'; id: string }

export function derekLegacyIssuesQuery(reportId: string) {
  return queryOptions({
    queryKey: ['report', reportId, 'derek-legacy'],
    queryFn: async () => {
      const res = await api.api.reports[':id'].derek.legacy.$get({ param: { id: reportId } })
      if (!res.ok) return fail(res, 'Failed to load the legacy sends')
      return res.json()
    },
    staleTime: 60 * 1000,
  })
}

function derekLegacyAgreementQuery(reportId: string, legacyId: string) {
  return queryOptions({
    queryKey: ['report', reportId, 'derek-legacy', legacyId],
    queryFn: async () => {
      const res = await api.api.reports[':id'].derek.legacy[':legacyId'].$get({
        param: { id: reportId, legacyId },
      })
      if (!res.ok) return fail(res, 'Failed to load the Derek agreement')
      return res.json()
    },
    staleTime: 30 * 1000,
  })
}

/** The agreement for whichever base the tab chose — the caller disables it while there is none. */
export function derekBaseAgreementQuery(reportId: string, base: DerekBase | null) {
  return base?.kind === 'legacy'
    ? derekLegacyAgreementQuery(reportId, base.id)
    : derekAgreementQuery(base?.id ?? '')
}

/**
 * Run experiment on the Derek tab: judge one header's articles in the chosen base with the prompt
 * as typed (`POST /api/report-issues/:id/derek/experiment`, or the legacy send's twin under
 * `/api/reports/:id/derek/legacy/:legacyId/experiment`). Nothing is saved, so no invalidation.
 */
export function useDerekExperimentFor(reportId: string, base: DerekBase | null) {
  // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation
  return useMutation({
    mutationFn: async (input: DerekExperimentRequest): Promise<DerekExperimentResult> => {
      if (!base) throw new Error('Pick a report above to experiment against.')
      const res =
        base.kind === 'legacy'
          ? await api.api.reports[':id'].derek.legacy[':legacyId'].experiment.$post({
              param: { id: reportId, legacyId: base.id },
              json: input,
            })
          : await api.api['report-issues'][':id'].derek.experiment.$post({
              param: { id: base.id },
              json: input,
            })
      if (!res.ok) return fail(res, 'The experiment failed')
      return res.json()
    },
  })
}
